import { useState } from "react";
import { motion } from "framer-motion";
import { Quote, Trophy } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { testimonials } from "@/data/content";
import { SectionHeader } from "@/components/SectionHeader";

const initials = (name = "") => {
  const s = String(name)
    .replace(/[^A-Za-z\u0600-\u06FF ]/g, "")
    .trim();
  if (!s) return "★";
  return s
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
};

const ClientLogo = ({ c, lang }) => {
  const [failed, setFailed] = useState(false);
  if (c.logo && !failed) {
    return (
      <img
        data-testid={`testimonial-logo-${c.id}`}
        src={c.logo}
        alt={c.client[lang]}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="h-12 w-12 rounded-lg object-contain bg-white dark:bg-slate-100 p-1 border border-slate-200 dark:border-slate-700"
      />
    );
  }
  return (
    <span className="h-12 w-12 rounded-lg bg-slate-950 text-white font-heading font-black flex items-center justify-center text-sm shrink-0">
      {initials(c.client.en)}
    </span>
  );
};

export const Testimonials = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-16 sm:py-24"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          title={t.testimonials.title}
          subtitle={t.testimonials.subtitle}
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {testimonials.map((c, i) => (
            <motion.div
              key={c.id}
              id={`testimonial-${c.id}`}
              data-testid={`testimonial-card-${c.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="relative flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 hover:border-cyan-300 dark:hover:border-cyan-500/60 transition-colors scroll-mt-24"
            >
              <span className="absolute -top-3 end-4 h-8 w-8 rounded-full bg-slate-950 dark:bg-cyan-500 text-white text-sm font-bold flex items-center justify-center shadow">
                {i + 1}
              </span>
              <div className="flex items-center gap-3">
                <ClientLogo c={c} lang={lang} />
                <div className="min-w-0">
                  <div className="font-bold text-slate-950 dark:text-white text-sm leading-tight">
                    {c.client[lang]}
                  </div>
                  <div className="flex gap-0.5 mt-1 text-yellow-500">
                    {"★★★★★"}
                  </div>
                </div>
              </div>

              <Quote className="h-6 w-6 text-cyan-200 dark:text-cyan-500/40 mt-5 rotate-180 shrink-0" />
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400 flex-1">
                {c.text[lang]}
              </p>

              <div
                data-testid={`testimonial-badge-${c.id}`}
                className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-3 py-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400"
              >
                <Trophy className="h-3.5 w-3.5" />
                {c.badge[lang]}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
